import { uuid } from './uuid';
// ─── recurringBilling.js ──────────────────────────────────────────────────────
// Phase 24: Recurring billing utilities.
// Recurring invoices are stored as regular invoices with a `recurrence` field.

/**
 * Recurrence shape:
 * {
 *   active: true,
 *   frequency: 'monthly' | 'weekly' | 'biweekly' | 'quarterly',
 *   startDate: ISO string,
 *   nextDueDate: ISO string,
 *   endDate: ISO string | null,   // null = indefinite
 *   createdCount: 0,
 *   maxCount: null,               // null = no limit
 * }
 */

export const RECURRENCE_LABELS = {
  weekly:    'Weekly',
  biweekly:  'Every 2 weeks',
  monthly:   'Monthly',
  quarterly: 'Quarterly (every 3 months)',
};

/**
 * Calculate the next due date from a recurrence config.
 */
export function nextDueDate(recurrence) {
  if (!recurrence?.active || !recurrence.frequency) return null;

  const base = new Date(recurrence.nextDueDate || recurrence.startDate);
  const d = new Date(base);

  switch (recurrence.frequency) {
    case 'weekly':    d.setDate(d.getDate() + 7); break;
    case 'biweekly':  d.setDate(d.getDate() + 14); break;
    case 'monthly':   d.setMonth(d.getMonth() + 1); break;
    case 'quarterly': d.setMonth(d.getMonth() + 3); break;
    default: return null;
  }

  // Check end conditions
  if (recurrence.endDate && d > new Date(recurrence.endDate)) return null;
  if (recurrence.maxCount && recurrence.createdCount >= recurrence.maxCount) return null;

  return d.toISOString();
}

/**
 * Check if a recurring invoice is due (nextDueDate <= today).
 */
export function isRecurringDue(invoice) {
  if (!invoice?.recurrence?.active) return false;
  const next = invoice.recurrence.nextDueDate;
  if (!next) return false;
  return new Date(next) <= new Date();
}

/**
 * Clone a recurring invoice into a new draft invoice.
 * The caller is responsible for saving the new invoice and updating
 * the original invoice's recurrence.nextDueDate.
 */
export function generateRecurringInvoice(sourceInvoice) {
  const { recurrence, ...rest } = sourceInvoice;
  return {
    ...rest,
    id: uuid(),
    status: 'draft',
    invoiceNumber: null, // will be assigned by InvoiceContext
    createdAt: new Date().toISOString(),
    dueAt: recurrence?.frequency === 'monthly'
      ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString()
      : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    paidAt: null,
    sentAt: null,
    recurrence: null,
    _sourceInvoiceId: sourceInvoice.id,
  };
}
